import React from 'react';

import {ConnectorLimitsList} from 'src/components/connector-limits-form/connector-limits-list';
import {TemporaryLimitControls} from 'src/components/connector-limits-form/temporary-limit-controls';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton, SkeletonItem} from 'src/components/skeleton';

import Button from '@pb/kit-component-button';
import {FormSection} from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';

import type {OnChangeLimits, TempLimitsSettings} from 'src/modules/usage-limits/typedef';
import type {DictionaryItem} from 'src/openapi/admin/models/DictionaryItem';
import type {Error as ApiError} from 'src/openapi/admin/models/Error';
import type {Limit} from 'src/openapi/admin/models/Limit';
import type {CheckBoxChangeData} from 'src/typedef';

export type McpLimitChangeHandler =
    (limitId: string, newValue?: number) => void;

type Props = {
    dictionary: DictionaryItem[],
    isPremiumMcpEnabled: boolean,
    data: Limit[],
    getDefaultValue: (code: string) => number,
    onLimitChange: OnChangeLimits,
    editDisabled: boolean,
    isLoading: boolean,
    error?: ApiError | null,
    tempLimitsSettings: TempLimitsSettings,
    today: Date,
    changeTemporaryLimit: OnChangeLimits,
    clearTemporaryLimit: (limitId: string) => void,
    clearCustomLimit: (limitId: string) => void,
    changeTemporaryLimitMode: (
        changeData: CheckBoxChangeData,
    ) => void,
    onExpiresOnChange: (expiresOn: string) => void,
    isDefaultLimits: boolean,
    onResetToDefaults: () => void,
    tooltipText: string,
};

export const McpLimitsForm = ({
    dictionary,
    isPremiumMcpEnabled,
    data,
    getDefaultValue,
    onLimitChange,
    editDisabled,
    isLoading,
    error,
    tempLimitsSettings,
    today,
    changeTemporaryLimit,
    clearTemporaryLimit,
    clearCustomLimit,
    changeTemporaryLimitMode,
    onExpiresOnChange,
    isDefaultLimits,
    onResetToDefaults,
    tooltipText,
}: Props) => (
    <ServerResponseHandler
        serverError={error || null}
        sectionName="Connector Limits">
        <Skeleton
            shown={isLoading}
            view={<SkeletonItem width={300} height={56}/>}>
            <FormSection>
                <Group direction="v" gap="m">
                    <Group>
                        <Button
                            variant="Type.SECONDARY"
                            disabled={
                                editDisabled
                                || isDefaultLimits
                            }
                            onClick={onResetToDefaults}>
                            Use default limits
                        </Button>
                        {isDefaultLimits ? (
                            <div>
                                Currently using
                                default limits.
                            </div>
                        ) : (
                            <div>
                                <b>
                                    Currently using
                                    custom limits.
                                </b>{' '}
                                Reset to defaults.
                            </div>
                        )}
                    </Group>
                    <TemporaryLimitControls
                        editDisabled={editDisabled}
                        expiresOn={tempLimitsSettings.expiresOn}
                        dateInputId="account-temp-mcp-limits-until-date-picker"
                        today={today}
                        changeTemporaryLimitMode={
                            changeTemporaryLimitMode
                        }
                        onExpiresOnChange={onExpiresOnChange}
                    />
                    <ConnectorLimitsList
                        editDisabled={editDisabled}
                        isPremiumMcpEnabled={isPremiumMcpEnabled}
                        mcpLimitsDictionary={dictionary}
                        data={data}
                        tempLimitsSettings={tempLimitsSettings}
                        tooltipText={tooltipText}
                        getDefaultValue={getDefaultValue}
                        onLimitChange={onLimitChange}
                        changeTemporaryLimit={changeTemporaryLimit}
                        clearTemporaryLimit={clearTemporaryLimit}
                        clearCustomLimit={clearCustomLimit}
                    />
                </Group>
            </FormSection>
        </Skeleton>
    </ServerResponseHandler>
);
